import { useOrganizationContext } from '@/contexts/OrganizationContext'
import { useAdmin } from '@/hooks/useAdmin'

const INACTIVE_STATUSES = ['suspended', 'canceled']

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  suspended: { label: 'Suspensa',  color: '#d97706', bg: 'rgba(251,191,36,0.08)',  border: 'rgba(251,191,36,0.25)' },
  canceled:  { label: 'Cancelada', color: '#dc2626', bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.25)' },
}

interface OrgStatusGuardProps {
  children: React.ReactNode
}

/**
 * Guard de status da organização:
 * - Suspensa/cancelada + usuário NÃO admin → tela de bloqueio
 * - Suspensa/cancelada + usuário admin      → banner de aviso + acesso normal
 */
export function OrgStatusGuard({ children }: OrgStatusGuardProps) {
  const { currentOrg, loading: orgLoading } = useOrganizationContext()
  const { isAdmin, loading: adminLoading } = useAdmin()

  if (orgLoading || adminLoading || !currentOrg) return <>{children}</>

  const isInactive = INACTIVE_STATUSES.includes(currentOrg.status ?? '')
  if (!isInactive) return <>{children}</>

  const cfg = STATUS_CONFIG[currentOrg.status ?? ''] ?? STATUS_CONFIG.suspended
  const isSuspended = currentOrg.status === 'suspended'

  // ── Admin: banner de aviso + conteúdo normal ──────────────────────────────
  if (isAdmin) {
    return (
      <>
        <div style={{
          position: 'sticky', top: 0, zIndex: 100,
          background: isSuspended
            ? 'linear-gradient(135deg, #78350f 0%, #b45309 50%, #d97706 100%)'
            : 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 50%, #dc2626 100%)',
          color: '#fff',
          padding: '0 20px',
          display: 'flex', alignItems: 'center', gap: 12,
          fontSize: 13, fontWeight: 500,
          boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
          minHeight: 44,
        }}>
          {/* Ícone */}
          <div style={{
            flexShrink: 0,
            width: 28, height: 28, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>

          {/* Texto */}
          <span style={{ flex: 1 }}>
            <strong>Visão de administrador</strong>
            {' — '}
            Organização <strong>"{currentOrg.name}"</strong> está{' '}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'rgba(255,255,255,0.18)', borderRadius: 20,
              padding: '1px 8px', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
              {cfg.label}
            </span>
            . Usuários comuns <strong>não têm acesso</strong> a esta organização.
          </span>
        </div>
        {children}
      </>
    )
  }

  // ── Usuário comum: tela de bloqueio ───────────────────────────────────────
  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at top, #f1f5f9 0%, #e2e8f0 100%)',
      padding: 24,
    }}>
      <div style={{
        maxWidth: 460, width: '100%', textAlign: 'center',
        padding: '48px 36px 40px',
        borderRadius: 24,
        background: '#fff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 8px 40px rgba(0,0,0,0.09), 0 2px 8px rgba(0,0,0,0.04)',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Faixa decorativa no topo */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 4,
          background: isSuspended
            ? 'linear-gradient(90deg, #f59e0b, #d97706)'
            : 'linear-gradient(90deg, #f87171, #dc2626)',
          borderRadius: '24px 24px 0 0',
        }} />

        {/* Ícone */}
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: cfg.bg,
          border: `2px solid ${cfg.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={cfg.color} strokeWidth="1.8">
            {isSuspended ? (
              <>
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </>
            ) : (
              <>
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </>
            )}
          </svg>
        </div>

        {/* Logo da org */}
        {currentOrg.logo_url ? (
          <img
            src={currentOrg.logo_url} alt={currentOrg.name}
            style={{ height: 28, maxWidth: 120, objectFit: 'contain', margin: '0 auto 14px', display: 'block', opacity: 0.7 }}
          />
        ) : (
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10 }}>
            {currentOrg.name}
          </p>
        )}

        {/* Status pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 14px', borderRadius: 999,
          background: cfg.bg, border: `1px solid ${cfg.border}`,
          fontSize: 11, fontWeight: 700, color: cfg.color,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          marginBottom: 20,
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: cfg.color, display: 'inline-block',
            animation: 'pulse-dot 2s ease-in-out infinite',
          }} />
          {cfg.label}
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 10, letterSpacing: '-0.02em' }}>
          Acesso indisponível
        </h1>
        <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, marginBottom: 32 }}>
          Esta organização está <strong style={{ color: cfg.color }}>{cfg.label.toLowerCase()}</strong> e
          não está aceitando acessos no momento.
          <br />
          Entre em contato com o suporte para regularizar.
        </p>

        {/* Ações */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <a
            href="mailto:suporte@fluity.com.br"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '13px 24px', borderRadius: 999,
              background: '#0f172a', color: '#fff',
              fontSize: 13.5, fontWeight: 600, textDecoration: 'none',
              transition: 'opacity .15s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ flexShrink: 0 }}>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Contatar suporte
          </a>
          <button
            onClick={() => { window.location.href = '/login' }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '13px 24px', borderRadius: 999,
              background: 'transparent', color: '#64748b',
              border: '1.5px solid #e2e8f0', fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ flexShrink: 0 }}>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sair da conta
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(0.7); }
        }
      `}</style>
    </div>
  )
}
